import CountdownTimer from "./CountdownTimer";
import { Cloud, Shield, Server, HardDrive, Network, Workflow, Database } from "lucide-react";

const learnTopics = [
  { icon: Shield, text: "Governance, identity (Entra ID) and monitoring" },
  { icon: Server, text: "Compute, storage, databases and data integration" },
  { icon: Network, text: "Networking: hub-and-spoke, ExpressRoute, Private Link, Azure Firewall" },
  { icon: Workflow, text: "Business continuity, migration and AI workloads" },
  { icon: Database, text: "Oracle on Azure: Oracle Database@Azure, Azure-OCI Interconnect, Oracle on Azure VMs, Data Guard, WebLogic" },
];

const highlights = [
  "60 hours, hands-on labs in every module",
  "Azure CLI, Bicep and Terraform",
  "Real case studies and a capstone design review",
  "AZ-305 exam preparation and practice questions",
  "For OCI, database and infrastructure architects moving to multicloud",
];

export default function AzureCourseSection() {
  return (
    <section id="azure-course" data-testid="azure-course-section" className="py-20 px-4 sm:px-6 lg:px-8 bg-[#111118]">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-2">
          <Cloud size={24} className="text-[#0078D4]" />
          <p className="font-mono text-xs text-[#0078D4] uppercase tracking-widest">// new batch &middot; microsoft azure az-305</p>
        </div>
        <h2 className="font-['Space_Grotesk'] text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold text-[#F4F4F5] mb-2">
          Azure Solutions Architect{" "}
          <span className="text-[#0078D4]">for OCI Architects</span>
        </h2>
        <p className="text-base text-[#A1A1AA] max-w-2xl mb-8">
          Already designing on Oracle Cloud? Reach Azure Solutions Architect Expert level, with every Azure service mapped to the OCI service you already know.
        </p>

        {/* Free demo banner */}
        <div className="mb-8 p-4 bg-[#0078D4]/10 border border-[#0078D4]/30 rounded-md inline-flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <div className="text-center px-3 py-1 bg-[#0078D4]/20 border border-[#0078D4]/30 rounded-md">
              <p className="text-[10px] text-[#0078D4] uppercase font-mono">Nov</p>
              <p className="font-['Space_Grotesk'] text-2xl font-bold text-[#F4F4F5] leading-none">10</p>
              <p className="text-[10px] text-[#71717A]">2026 &middot; Tue</p>
            </div>
          </div>
          <div>
            <p className="font-['Space_Grotesk'] font-bold text-[#F4F4F5] text-sm">
              Free demo &middot; 8:30 – 9:00 AM IST
            </p>
            <p className="text-xs text-[#0078D4]">
              Course starts <span className="font-semibold text-[#FF9900]">the same day, same time</span>
            </p>
          </div>
        </div>

        {/* Countdown + Details row */}
        <div className="grid lg:grid-cols-2 gap-6 mb-12">
          <div className="p-6 bg-[#16161F] border border-[#27272A] rounded-md">
            <h3 className="font-['Space_Grotesk'] text-sm font-semibold text-[#0078D4] uppercase tracking-wider mb-4">Course Details</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between text-[#A1A1AA]">
                <span className="text-[#71717A]">Duration</span>
                <span>60 Hours</span>
              </div>
              <div className="flex justify-between text-[#A1A1AA]">
                <span className="text-[#71717A]">Free Demo</span>
                <span className="text-[#0078D4] font-semibold">10-Nov-2026 (Tuesday)</span>
              </div>
              <div className="flex justify-between text-[#A1A1AA]">
                <span className="text-[#71717A]">Start Date</span>
                <span>10-Nov-2026</span>
              </div>
              <div className="flex justify-between text-[#A1A1AA]">
                <span className="text-[#71717A]">Time</span>
                <span>08:30 – 09:00 AM IST (demo)</span>
              </div>
              <div className="flex justify-between text-[#A1A1AA]">
                <span className="text-[#71717A]">Exam Prep</span>
                <span>AZ-305</span>
              </div>
              <div className="flex justify-between text-[#A1A1AA]">
                <span className="text-[#71717A]">Contact</span>
                <span className="text-[#22D3EE]">+91-8978266997</span>
              </div>
            </div>
          </div>

          <CountdownTimer targetDate="2026-11-10T08:30:00+05:30" label="Azure Course Starts In" accentColor="#0078D4" />
        </div>

        {/* What you'll learn + Highlights */}
        <div className="grid md:grid-cols-2 gap-4">
          <div className="p-6 bg-[#16161F] border border-[#27272A] rounded-md">
            <h3 className="font-['Space_Grotesk'] text-sm font-semibold text-[#0078D4] uppercase tracking-wider mb-4">What You'll Learn</h3>
            <ul className="space-y-3">
              {learnTopics.map((topic, i) => {
                const Icon = topic.icon;
                return (
                  <li key={i} className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-7 h-7 flex items-center justify-center rounded-md bg-[#0078D4]/10 border border-[#0078D4]/20 mt-0.5">
                      <Icon size={14} className="text-[#0078D4]" />
                    </div>
                    <span className="text-sm text-[#A1A1AA] leading-relaxed">{topic.text}</span>
                  </li>
                );
              })}
            </ul>
          </div>

          <div className="p-6 bg-[#16161F] border border-[#27272A] rounded-md">
            <h3 className="font-['Space_Grotesk'] text-sm font-semibold text-[#0078D4] uppercase tracking-wider mb-4">Course Highlights</h3>
            <ul className="space-y-3">
              {highlights.map((item, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-[#A1A1AA]">
                  <span className="text-[#0078D4] flex-shrink-0 mt-0.5">&#8226;</span>
                  <span className="leading-relaxed">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}
